import React from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { usePlatformAccess } from "../../hooks/usePlatformAccess";

/**
 * Gate for every /platform-admin route.
 *
 * Order matters and is deliberate:
 *   1. while the session is unknown, render a neutral Bulgarian waiting state;
 *   2. no session -> the login screen;
 *   3. session but access not yet verified -> keep waiting. NO platform data is
 *      requested or rendered before the backend confirmed access;
 *   4. verified non-administrator -> a Bulgarian access-denied screen;
 *   5. active platform super administrator -> the console.
 *
 * The gate never inspects an organization role and never requires organization
 * membership.
 */
const Waiting = ({ label, testId }) => (
  <div className="qw-platform-gate" data-testid={testId}>
    {label}
  </div>
);

export const PlatformAccessGate = ({ children }) => {
  const { t } = useTranslation();
  const { loading: authLoading, session } = useAuth();
  const { loading, isPlatformAdmin } = usePlatformAccess();

  if (authLoading) {
    return <Waiting label={t("common.loading")} testId="platform-session-loading" />;
  }
  if (!session) return <Navigate to="/login" replace />;
  if (loading) {
    return <Waiting label={t("platform.checking_access")} testId="platform-access-loading" />;
  }
  if (!isPlatformAdmin) {
    return (
      <div className="qw-platform-gate" data-testid="platform-access-denied">
        <div className="qw-card" style={{ maxWidth: 460 }}>
          <div className="hstack" style={{ gap: 10, marginBottom: 8 }}>
            <ShieldAlert size={18} strokeWidth={2} aria-hidden="true" />
            <strong data-testid="platform-access-denied-title">
              {t("platform.denied_title")}
            </strong>
          </div>
          <div className="qw-help" style={{ marginBottom: 14 }}>
            {t("platform.denied_body")}
          </div>
          <Link to="/dashboard" className="qw-btn primary" data-testid="platform-denied-back">
            {t("platform.back_to_app")}
          </Link>
        </div>
      </div>
    );
  }
  return children;
};

export default PlatformAccessGate;
